import {
  createCostingViewForBuilding,
  createCostingViewForTotal,
} from "src/service/cost/utility/custom-view";
import { calculateValuePerMetrics } from "src/service/cost/data/value-per-metric";
import { localizeMetric } from "./localizeMetric";
import { useCurrencySymbol } from "src/hooks/useCurrencySymbol";
import { sum } from "src/service/core/data/number";
import type { Unit } from "../UnitSelect";
import { Table } from "../Table";
import type { Building } from "src/types/Building";
import type { World } from "src/types/World";
import type { MetricsCalculations } from "src/hooks/useMetricsCalculationsQuery";
import { useTranslation } from "src/translations";

interface Row {
  label: string;
  cost: number;
  perUnit: number;
  perMetric: number;
}

type Translator = (key: string, vars?: Record<string, string | number>) => string;

export function SummaryTable(props: {
  unit: Unit;
  metricsCalculations: MetricsCalculations;
  selectedBuilding: Building | null;
  design: World;
}) {
  const { unit } = props;
  const currency = useCurrencySymbol();
  const { t } = useTranslation();

  const rows = useRows(props.design, props.selectedBuilding, props.metricsCalculations, unit, t);

  return (
    <div>
      <Table.Sticky>
        <Table.Head label={t("costSummary.title")} variant="section" />
        <Table.Row>
          <Table.Head label="" variant="header" position="start" />
          <Table.Head
            label={t("costSummary.cost")}
            symbol={`(${currency})`}
            variant="header"
            position="end"
          />
          <Table.Head
            label={t("costSummary.perUnit")}
            symbol={`(${currency})`}
            variant="header"
            position="end"
          />
          <Table.Head
            label={`${t("costSummary.per")}${unit}`}
            symbol={`(${currency})`}
            variant="header"
            position="end"
          />
        </Table.Row>
      </Table.Sticky>
      <TableRow row={rows.modular} variant="cell" />
      <TableRow row={rows.fitOut} variant="cell" />
      <TableRow row={rows.baseBuild} variant="cell" />
      <TableRow row={rows.construction} variant="subtotal" currency={currency} />
      <TableRow
        row={rows.preliminaries}
        variant="cell"
        labelTitle={t("costSummary.prelimsTooltip")}
      />
      <TableRow
        row={rows.contingency}
        variant="cell"
        labelTitle={t("costSummary.contingencyTooltip")}
      />
      <TableRow
        row={rows.overheadsAndProfit}
        variant="cell"
        labelTitle={t("costSummary.ohpTooltip")}
      />
      <TableRow
        row={rows.total}
        variant="total"
        currency={currency}
        labelTitle={t("costSummary.totalTooltip")}
      />
    </div>
  );
}

function TableRow(props: {
  row: Row;
  variant: "cell" | "subtotal" | "total";
  labelTitle?: string;
  currency?: string;
}) {
  return (
    <Table.Row>
      <Table.Col>
        <Table.Label label={props.row.label} variant={props.variant} title={props.labelTitle} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.cost} variant={props.variant} currency={props.currency} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.perUnit} variant={props.variant} currency={props.currency} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.perMetric} variant={props.variant} currency={props.currency} />
      </Table.Col>
    </Table.Row>
  );
}

function useRows(
  design: World,
  selectedBuilding: Building | null,
  metricsCalculations: MetricsCalculations,
  unit: Unit,
  t: Translator
): {
  total: Row;
  modular: Row;
  fitOut: Row;
  baseBuild: Row;
  construction: Row;
  preliminaries: Row;
  contingency: Row;
  overheadsAndProfit: Row;
} {
  const unitCount =
    selectedBuilding?.metrics.ApartmentQuantityTotal ??
    design.architecturalMetrics.ApartmentQuantityTotal;

  const gia = selectedBuilding?.metrics.GIATotal ?? design.architecturalMetrics.GIATotal;

  const costingView = selectedBuilding
    ? createCostingViewForBuilding(metricsCalculations, design.buildings.indexOf(selectedBuilding))
    : createCostingViewForTotal(metricsCalculations);

  const constructionFitOutValuePerMetric = calculateValuePerMetrics(
    costingView.summary.constructionFitOut,
    unitCount,
    gia
  );

  const constructionModulesValuePerMetric = calculateValuePerMetrics(
    costingView.summary.constructionModules,
    unitCount,
    gia
  );

  const constructionBaseBuildValuePerMetric = calculateValuePerMetrics(
    costingView.summary.constructionBaseBuild,
    unitCount,
    gia
  );

  const constructionSubTotalValue = sum([
    costingView.summary.constructionModules,
    costingView.summary.constructionFitOut,
    costingView.summary.constructionBaseBuild,
  ]);

  const constructionSubTotalValuePerMetric = calculateValuePerMetrics(
    constructionSubTotalValue,
    unitCount,
    gia
  );

  const onCostPreliminariesValuePerMetric = calculateValuePerMetrics(
    costingView.summary.preliminaries,
    unitCount,
    gia
  );

  const onCostContingencyValuePerMetric = calculateValuePerMetrics(
    costingView.summary.contingencies,
    unitCount,
    gia
  );

  const onCostOverheadsAndProfitValuePerMetric = calculateValuePerMetrics(
    costingView.summary.overheadsAndProfits,
    unitCount,
    gia
  );

  const construction = sum([
    costingView.summary.constructionModules,
    costingView.summary.constructionFitOut,
    costingView.summary.constructionBaseBuild,
  ]);

  const totalPerUnit = calculateValuePerMetrics(costingView.summary.total, unitCount, gia);

  return {
    total: {
      label: t("common.total"),
      cost: costingView.summary.total,
      perUnit: totalPerUnit.unit,
      perMetric: localizeMetric(totalPerUnit, unit),
    },
    modular: {
      label: t("costSummary.modular"),
      cost: costingView.summary.constructionModules,
      perUnit: constructionModulesValuePerMetric.unit,
      perMetric: localizeMetric(constructionModulesValuePerMetric, unit),
    },
    fitOut: {
      label: t("costSummary.fitOut"),
      cost: costingView.summary.constructionFitOut,
      perUnit: constructionFitOutValuePerMetric.unit,
      perMetric: localizeMetric(constructionFitOutValuePerMetric, unit),
    },
    baseBuild: {
      label: t("costSummary.baseBuild"),
      cost: costingView.summary.constructionBaseBuild,
      perUnit: constructionBaseBuildValuePerMetric.unit,
      perMetric: localizeMetric(constructionBaseBuildValuePerMetric, unit),
    },
    construction: {
      label: t("costSummary.construction"),
      cost: construction,
      perUnit: constructionSubTotalValuePerMetric.unit,
      perMetric: localizeMetric(constructionSubTotalValuePerMetric, unit),
    },
    preliminaries: {
      label: t("costSummary.prelims"),
      cost: costingView.summary.preliminaries,
      perUnit: onCostPreliminariesValuePerMetric.unit,
      perMetric: localizeMetric(onCostPreliminariesValuePerMetric, unit),
    },
    contingency: {
      label: t("costSummary.contingency"),
      cost: costingView.summary.contingencies,
      perUnit: onCostContingencyValuePerMetric.unit,
      perMetric: localizeMetric(onCostContingencyValuePerMetric, unit),
    },
    overheadsAndProfit: {
      label: t("costSummary.ohp"),
      cost: costingView.summary.overheadsAndProfits,
      perUnit: onCostOverheadsAndProfitValuePerMetric.unit,
      perMetric: localizeMetric(onCostOverheadsAndProfitValuePerMetric, unit),
    },
  };
}
